import { useState, useEffect } from 'react';
import { MapPin, Search, PieChart, Calendar, User, Clock, ShoppingCart, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
export default function ManagerDashboard() {
  const API_URL = process.env.REACT_APP_API_URL;
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const firstName = storedUser?.firstName || "User";
  const token = storedUser?.token;

  const navigate = useNavigate();

  // States
  const [showFilters, setShowFilters] = useState(false);
  const [workCategories, setWorkCategories] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTradesman, setSelectedTradesman] = useState(null);
  const [locations, setLocations] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filters, setFilters] = useState({ user: "", timeFrom: "", timeTo: "", categories: [], location: [], });
  const [filterError, setFilterError] = useState(null);
  const [dateError, setDateError] = useState(null);

  // Data states
  const [tradesmen, setTradesmen] = useState([]);
  const [dailyEntries, setDailyEntries] = useState([]);
  const [tradesmanDetails, setTradesmanDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all tradesmen on mount
  useEffect(() => {
    fetchTradesmen();
    fetchCategories();
    fetchLocations();
  }, []);

  // Fetch daily entries when date or filters change
  useEffect(() => {
    fetchDailyEntries();
  }, [selectedDate]);

  // Fetch tradesman details when selection changes
  useEffect(() => {
    if (selectedTradesman) {
      fetchTradesmanDetails();
    } else {
      setTradesmanDetails(null);
    }
  }, [selectedTradesman, dateRange]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  useEffect(() => {
    if (filterError || dateError) {
      const t = setTimeout(() => {
        setFilterError(null);
        setDateError(null);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [filterError, dateError]);



  // API Calls
  const fetchTradesmen = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/manager/tradesmen`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch tradesmen');
      const data = await response.json();
      setTradesmen(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching tradesmen:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/manager/categories`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      setWorkCategories(data);
    } catch (error) {
      console.error("Error fetching work categories:", error);
    }
  };

  const fetchDailyEntries = async () => {
    try {
      setLoading(true);
      //const dateStr = selectedDate.toISOString().split('T')[0];
      const dateStr = formatLocalDate(selectedDate);


      // Build query params
      const params = new URLSearchParams({ date: dateStr });
      if (filters.user) params.append('user', filters.user);
      if (filters.timeFrom) params.append('timeFrom', filters.timeFrom);
      if (filters.timeTo) params.append('timeTo', filters.timeTo);
      filters.categories.forEach(cat => params.append('categories', cat));
      filters.location.forEach(locId => params.append('location', locId))

      const response = await authFetch(`${API_URL}/api/manager/work-entries/date?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch daily entries');
      const data = await response.json();
      setDailyEntries(data.entries || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching daily entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradesmanDetails = async () => {
    if (!selectedTradesman) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        startDate: dateRange.from,
        endDate: dateRange.to
      });

      const response = await authFetch(
        `${API_URL}/api/manager/tradesmen/${selectedTradesman.id}/details?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch tradesman details');
      const data = await response.json();
      setTradesmanDetails(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching tradesman details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/manager/locations`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
      if (!res.ok) throw new Error("Failed to fetch locations");
      const data = await res.json();
      setLocations(data);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };


  // Filter handlers

  const applyFilters = () => {
    // Time validation
    if (filters.timeFrom && filters.timeTo) {
      if (filters.timeFrom >= filters.timeTo) {
        setFilterError("Start time must be earlier than end time.");
        return;
      }
    }

    setFilterError(null); // clear previous errors
    fetchDailyEntries();
    setShowFilters(false);
  };




  const clearFilters = () => {
    setFilters({
      user: "",
      timeFrom: "",
      timeTo: "",
      categories: [],
      location: [],
    });
    setTimeout(fetchDailyEntries, 0);
  };

  // Calendar navigation
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString("default", { month: "long" });
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startIndex = (firstDayOfMonth + 6) % 7;
  const daysArray = Array(startIndex).fill(null).concat([...Array(daysInMonth)].map((_, i) => i + 1));

  const goToPrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  // Filtered tradesmen for search
  const filteredTradesmen = tradesmen.filter(t =>
    `${t.firstName} ${t.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format helpers
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };


  const formatSelectedDate = () =>
    selectedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const formatTime = (time) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  useEffect(() => {
  if (tradesmanDetails) {
    console.log("TRADESMAN DETAILS 👉", tradesmanDetails);
    console.log(
      "LOCATION BREAKDOWN 👉",
      tradesmanDetails?.workBreakdown?.locationBreakdown
    );
  }
}, [tradesmanDetails]);


  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-1 max-w-full mx-auto w-full p-4 md:p-6">

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
  <div className="flex items-start justify-between">
    
    {/* LEFT */}
    <div>
      <h1 className="text-3xl font-bold text-blue-900 mb-2">
        Welcome {firstName}
      </h1>
      <p className="text-gray-600">
        Manage your team and track their progress
      </p>
    </div>

    {/* RIGHT BUTTON */}
    <button
      onClick={() => navigate("/manager-settings")}
      className="flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-4 py-2 rounded-lg shadow transition"
    >
      <Settings className="w-5 h-5" />
      Settings
    </button>

  </div>
</div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button onClick={goToPrevMonth} className="bg-blue-900 text-white p-2 rounded hover:bg-blue-800">
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <h2 className="text-xl font-bold text-blue-900">Select Date</h2>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar className="w-5 h-5 text-yellow-500" />
                <span className="font-semibold capitalize">{monthName} {year}</span>
                <button onClick={goToNextMonth} className="bg-blue-900 text-white p-2 rounded hover:bg-blue-800">
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">{day}</div>
              ))}

              {daysArray.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} className="p-3"></div>;

                const today = new Date();
                const isSelected = selectedDate.getDate() === day && selectedDate.getMonth() === month && selectedDate.getFullYear() === year;
                const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                const isFuture = new Date(year, month, day) > today;

                return (
                  <button
                    key={day}
                    onClick={() => !isFuture && setSelectedDate(new Date(year, month, day))}
                    disabled={isFuture}
                    className={`p-3 rounded-lg text-center transition-colors ${isFuture ? 'bg-gray-100 text-gray-300 cursor-not-allowed' :
                      isSelected ? 'bg-yellow-500 text-white font-bold shadow-md' :
                        isToday ? 'bg-blue-100 text-blue-900 font-semibold hover:bg-yellow-100' :
                          'bg-gray-50 text-gray-700 hover:bg-yellow-100'
                      }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-4">All Entries - {formatSelectedDate()}</h2>

            <div className="mb-4 border border-gray-200 rounded-xl bg-white shadow-sm">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-t-xl transition-colors"
              >
                <div className="flex items-center gap-2 text-blue-900 font-semibold">
                  <Search className="w-5 h-5 text-yellow-500" />
                  <span>Filters</span>
                </div>
                {showFilters ? <ChevronLeft className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />}
              </button>

              {showFilters && (
                <div className="p-4 space-y-4 border-t border-gray-100">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={filters.user}
                      onChange={(e) => setFilters({ ...filters, user: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                      <input
                        type="time"
                        value={filters.timeFrom}
                        onChange={(e) => setFilters({ ...filters, timeFrom: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                      <input
                        type="time"
                        value={filters.timeTo}
                        onChange={(e) => setFilters({ ...filters, timeTo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                    <div className="flex flex-wrap gap-3">
                      {workCategories.map((cat) => (
                        <label
                          key={cat.id}
                          className={`flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full cursor-pointer transition-colors ${filters.categories.includes(cat.name)
                            ? "bg-yellow-100 text-yellow-800 border border-yellow-400"
                            : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={filters.categories.includes(cat.name)}
                            onChange={(e) => {
                              const newCategories = e.target.checked
                                ? [...filters.categories, cat.name]
                                : filters.categories.filter((c) => c !== cat.name);
                              setFilters({ ...filters, categories: newCategories });
                            }}
                            className="hidden"
                          />
                          {cat.name}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Locations
                    </label>

                    <div className="flex flex-wrap gap-3">
                      {locations.map((loc) => (
                        <label
                          key={loc.id}
                          className={`flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full cursor-pointer transition-colors ${filters.location.includes(loc.id)
                            ? "bg-blue-100 text-blue-800 border border-blue-400"
                            : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={filters.location.includes(loc.id)}
                            onChange={(e) => {
                              const newLocations = e.target.checked
                                ? [...filters.location, loc.id]
                                : filters.location.filter((id) => id !== loc.id);
                              setFilters({ ...filters, location: newLocations });
                            }}
                            className="hidden"
                          />
                          {loc.name}
                        </label>
                      ))}
                    </div>
                  </div>

                  {filterError && (
                    <div className="text-red-600 text-sm font-semibold p-2 bg-red-50 border border-red-200 rounded">
                      {filterError}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={applyFilters}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      Apply
                    </button>
                    <button
                      onClick={clearFilters}
                      className="border border-gray-300 hover:bg-gray-100 text-gray-800 font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
                </div>
              ) : dailyEntries.length > 0 ? (
                dailyEntries.map((entry) => (
                  <div key={entry.id} className="border-l-4 bg-gray-50 p-4 rounded-r-lg hover:shadow-md transition-shadow" style={{ borderColor: entry.categoryColor }}>
                    <div className="flex flex-col gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-600" />
                        <span className="font-bold text-blue-900">{entry.firstName} {entry.lastName}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">

                        {/* LEFT — TIME */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">
                            {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({entry.durationHours.toFixed(1)}h)
                          </span>
                        </div>

                        {/* RIGHT — LOCATION + CATEGORY */}
                        <div className="flex items-center gap-2">

                          {/* LOCATION */}
                          {entry.locationName && (
                            <span className="flex items-center gap-1 text-xs px-3 py-1 rounded-full font-semibold bg-blue-100 text-blue-800">
                              <MapPin className="w-3 h-3" />
                              {entry.locationName}
                            </span>
                          )}

                          {/* CATEGORY */}
                          <span
                            className="text-xs px-3 py-1 rounded-full font-semibold"
                            style={{
                              backgroundColor: `${entry.categoryColor}20`,
                              color: entry.categoryColor
                            }}
                          >
                            {entry.category}
                          </span>

                        </div>
                      </div>

                    </div>
                    <p className="text-gray-700 text-sm">{entry.description}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No entries for this date</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-4">Search Tradesman</h2>

            <div className="mb-4 relative">
              <label className="block text-gray-700 text-sm font-semibold mb-2">Search by Name/Surname</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Type tradesman name..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(e.target.value.length > 0);
                  }}
                  onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none"
                />

                {showSuggestions && filteredTradesmen.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredTradesmen.map((tradesman) => (
                      <button
                        key={tradesman.id}
                        onClick={() => {
                          setSelectedTradesman(tradesman);
                          setSearchQuery(`${tradesman.firstName} ${tradesman.lastName}`);
                          setShowSuggestions(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-600" />
                          <span className="font-semibold text-blue-900">{tradesman.firstName} {tradesman.lastName}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-semibold mb-2">Time Range</label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-gray-600 text-xs mb-1">From</label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      // validate only if user typed something
                      if (dateRange.to && newFrom > dateRange.to) {
                        setDateError("Start date cannot be later than end date.");
                        return;
                      }
                      setDateError(null);
                      setDateRange({ ...dateRange, from: newFrom });
                    }}

                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-600 text-xs mb-1">To</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const newTo = e.target.value;
                      // validate only if user typed something
                      if (dateRange.from && newTo < dateRange.from) {
                        setDateError("End date cannot be earlier than start date.");
                        return;
                      }
                      setDateError(null);
                      setDateRange({ ...dateRange, to: newTo });
                    }}

                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
            </div>
            {dateError && (
              <div className="mt-2 text-red-600 text-sm font-semibold p-2 bg-red-50 border border-red-200 rounded">
                {dateError}
              </div>
            )}

            {selectedTradesman && (
              <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-5 h-5 text-yellow-600" />
                  <span className="font-bold text-blue-900">{selectedTradesman.firstName} {selectedTradesman.lastName}</span>
                </div>
                <p className="text-sm text-gray-600">Period: {formatDate(dateRange.from)} - {formatDate(dateRange.to)}</p>
                {tradesmanDetails && (
                  <p className="text-sm text-gray-600">{tradesmanDetails.totalEntries} entries recorded</p>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-6 h-6 text-yellow-500" />
              <h2 className="text-xl font-bold text-blue-900">Shopping List</h2>
            </div>
            {selectedTradesman ? (
              tradesmanDetails?.shoppingList?.content ? (
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                  <pre className="text-gray-700 text-sm whitespace-pre-wrap font-sans leading-relaxed">
                    {tradesmanDetails.shoppingList.content}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No shopping list available</p>
                </div>
              )
            ) : (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Select a tradesman to view their shopping list</p>
              </div>
            )}
          </div>
        </div>

        {selectedTradesman && tradesmanDetails && (
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="w-6 h-6 text-yellow-500" />
                <h2 className="text-xl font-bold text-blue-900">Work Breakdown</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Time distribution by category ({formatDate(dateRange.from)} - {formatDate(dateRange.to)})
              </p>

              {tradesmanDetails.workBreakdown?.categoryBreakdown?.length > 0 ? (
                <>
                  <div className="flex items-center justify-center h-64">
                    <svg viewBox="0 0 200 200" className="w-48 h-48">
                      {tradesmanDetails.workBreakdown.categoryBreakdown.map((item, index) => {
                        const prevPercentage = tradesmanDetails.workBreakdown.categoryBreakdown
                          .slice(0, index)
                          .reduce((sum, i) => sum + i.percentage, 0);
                        const circumference = 2 * Math.PI * 80;
                        const dashArray = `${(item.percentage / 100) * circumference} ${circumference}`;
                        const dashOffset = -((prevPercentage / 100) * circumference);

                        return (
                          <circle
                            key={index}
                            cx="100"
                            cy="100"
                            r="80"
                            fill="none"
                            stroke={item.color}
                            strokeWidth="40"
                            strokeDasharray={dashArray}
                            strokeDashoffset={dashOffset}
                            transform="rotate(-90 100 100)"
                          />
                        );
                      })}
                    </svg>
                  </div>

                  <div className="space-y-2 mt-4">
                    {tradesmanDetails.workBreakdown.categoryBreakdown.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm text-gray-700 font-medium">{item.category}</span>
                        </div>
                        <span className="font-bold text-blue-900">{item.hours}h ({item.percentage}%)</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <PieChart className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No work data available</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-6 h-6 text-blue-900" />
                <h2 className="text-xl font-bold text-blue-900">Aggregated Data</h2>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Summary for {selectedTradesman.firstName} {selectedTradesman.lastName}
              </p>

              <div className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg p-6 mb-4 text-white">
                <div className="text-sm font-semibold mb-2 opacity-90">Total Hours Worked</div>
                <div className="text-5xl font-bold mb-2">
                  {tradesmanDetails.totalHours}<span className="text-2xl">h</span>
                </div>
                <div className="text-sm opacity-90">
                  {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-500">
                  <div className="text-xs text-gray-600 mb-1">Total Entries</div>
                  <div className="text-2xl font-bold text-blue-900">{tradesmanDetails.totalEntries}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
                  <div className="text-xs text-gray-600 mb-1">Avg Hours/Day</div>
                  <div className="text-2xl font-bold text-blue-900">{tradesmanDetails.avgHoursPerDay}h</div>
                </div>
              </div>

              {tradesmanDetails.workBreakdown?.categoryBreakdown?.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Categories Worked</div>
                  <div className="flex flex-wrap gap-2">
                    {tradesmanDetails.workBreakdown.categoryBreakdown.map((item, index) => (
                      <span
                        key={index}
                        className="text-xs px-3 py-1 rounded-full font-semibold"
                        style={{
                          backgroundColor: `${item.color}20`,
                          color: item.color
                        }}
                      >
                        {item.category}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {tradesmanDetails.workBreakdown?.locationBreakdown?.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-semibold text-gray-700 mb-2">
                    Locations Worked
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {tradesmanDetails.workBreakdown.locationBreakdown.map((item, index) => (
                      <span
                        key={index}
                        className="flex items-center gap-1 text-xs px-3 py-1 rounded-full font-semibold bg-blue-100 text-blue-800"
                      >
                        <MapPin className="w-3 h-3" />
                        {item.locationName}
                        <span className="text-blue-600 font-normal">
                          ({item.hours}h)
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}



            </div>
          </div>
        )}
      </div>
    </div>
  );
}